import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { INVITATION_TYPES, invitationTypeLabel } from '../../lib/invitation-types.js';
import {
    invitableEntry,
    inviteWave,
    isPending,
    LISTS,
    listLabel,
    sourceSiteLabel,
} from '../../lib/lists.js';
import { css, html, nothing } from '../../lib/lit.js';

function formatDate(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

const UTM_FIELDS = [
    ['source', 'UTM source'],
    ['medium', 'UTM medium'],
    ['campaign', 'UTM campaign'],
    ['term', 'UTM term'],
    ['content', 'UTM content'],
];

// One person on the public lists: every list they're on, confirmed or not.
// Staff can invite them from early access (the same wave endpoint, with one
// person), take them off one list, or remove them from every list for a
// deletion request. The /beta-queue/<entry id> link from d3-community's Discord
// announcements (dice-driven/d3-community#38) redirects here.
class ListContactView extends BaseComponent {
    static properties = {
        contactId: { type: String, attribute: 'contact-id' },
        _contact: { state: true },
        _loading: { state: true },
        _error: { state: true },
        _type: { state: true },
        /** @type {{action: 'invite'|'remove-list'|'remove-all', list?: string}|null} */
        _confirming: { state: true },
        _acting: { state: true },
        _notice: { state: true },
        _actionError: { state: true },
        /** @type {string} Set once they're on no list at all, saying why. */
        _gone: { state: true },
    };

    static styles = css`
        :host {
            display: block;
        }
        .wrap {
            max-width: 48rem;
            margin: 0 auto;
            padding: 2rem 1rem 4rem;
        }
        dt {
            font-weight: normal;
        }
    `;

    constructor() {
        super();
        this.contactId = '';
        this._contact = null;
        this._loading = true;
        this._error = '';
        this._type = INVITATION_TYPES[0].slug;
        this._confirming = null;
        this._acting = false;
        this._notice = '';
        this._actionError = '';
        this._gone = '';
    }

    updated(changed) {
        if (changed.has('contactId') && this.contactId) {
            this._load();
        }
    }

    async _load() {
        this._loading = true;
        this._error = '';
        try {
            this._contact = await api.get(`/contacts/${encodeURIComponent(this.contactId)}`);
        } catch (error) {
            if (error.status === 404 && this._contact) {
                // Taking them off their last list deletes the address too.
                this._gone = `${this._contact.email} is no longer on any list, so the address was deleted.`;
            } else {
                this._contact = null;
                this._error =
                    error.status === 404
                        ? 'Not on any list: removed, or a sign-up that was never confirmed and has expired.'
                        : error.message;
            }
        } finally {
            this._loading = false;
        }
    }

    _confirm(confirming) {
        this._notice = '';
        this._actionError = '';
        this._confirming = confirming;
    }

    async _invite() {
        const entry = invitableEntry(this._contact);
        this._acting = true;
        const type = this._type;
        try {
            const [result] = await inviteWave((path, body) => api.post(path, body), type, [
                entry.id,
            ]);
            if (result.status === 'invited') {
                this._notice = `Invited as ${invitationTypeLabel(type)}.${result.detail ? ` (${result.detail})` : ''}`;
            } else {
                this._actionError = result.detail;
            }
        } finally {
            this._acting = false;
            this._confirming = null;
        }
        this._load();
    }

    async _removeFromList(list) {
        this._acting = true;
        try {
            const entry = this._contact.lists[list];
            await api.delete(`/lists/${list}/${encodeURIComponent(entry.id)}`);
            this._notice = `Removed from ${listLabel(list)}.`;
            this._load();
        } catch (error) {
            this._actionError = error.message;
        } finally {
            this._acting = false;
            this._confirming = null;
        }
    }

    async _removeFromAll() {
        this._acting = true;
        try {
            await api.delete(`/contacts/${encodeURIComponent(this._contact.id)}`);
            this._gone = `Removed ${this._contact.email} from every list and deleted the address.`;
        } catch (error) {
            this._actionError = error.message;
        } finally {
            this._acting = false;
            this._confirming = null;
        }
    }

    render() {
        return html`
            <div class="wrap">
                <a class="small" href="/lists"><i class="bi bi-arrow-left"></i> Lists</a>
                ${this._renderBody()}
            </div>
        `;
    }

    _renderBody() {
        if (this._gone) {
            return html`<div class="alert alert-success mt-3" role="status">${this._gone}</div>`;
        }
        if (this._loading && !this._contact) {
            return html`<p class="text-muted mt-3">Loading…</p>`;
        }
        if (this._error) {
            return html`<div class="alert alert-danger mt-3" role="alert">${this._error}</div>`;
        }

        const contact = this._contact;
        const lists = LISTS.filter((l) => contact.lists[l.value]);
        return html`
            <div class="d-flex flex-wrap align-items-center gap-2 mt-3 mb-3">
                <h1 class="h4 mb-0">${contact.email}</h1>
                ${this._renderRemoveAll()}
            </div>
            ${
                this._notice
                    ? html`<div class="alert alert-success" role="status">${this._notice}</div>`
                    : nothing
            }
            ${
                this._actionError
                    ? html`<div class="alert alert-danger" role="alert">${this._actionError}</div>`
                    : nothing
            }
            ${lists.map((l) => this._renderList(l.value, contact.lists[l.value]))}
        `;
    }

    _renderRemoveAll() {
        if (this._confirming?.action === 'remove-all') {
            return html`
                <div class="w-100 d-flex flex-wrap align-items-center gap-2 p-2 border border-danger rounded">
                    <span class="small">
                        Remove ${this._contact.email} from every list and delete the address? This is for a
                        deletion request and can't be undone.
                    </span>
                    ${this._renderConfirmButtons('Remove', 'Removing…', this._removeFromAll, 'btn-danger')}
                </div>
            `;
        }
        return html`<button
            class="btn btn-sm btn-outline-danger ms-auto"
            type="button"
            ?disabled=${this._acting || this._confirming}
            @click=${() => this._confirm({ action: 'remove-all' })}
        >
            Remove from every list
        </button>`;
    }

    _renderConfirmButtons(label, busyLabel, action, style) {
        return html`
            <button class="btn btn-sm ${style}" type="button" ?disabled=${this._acting} @click=${action}>
                ${this._acting ? busyLabel : label}
            </button>
            <button
                class="btn btn-sm btn-outline-secondary"
                type="button"
                ?disabled=${this._acting}
                @click=${() => {
                    this._confirming = null;
                }}
            >
                Cancel
            </button>
        `;
    }

    _renderList(list, entry) {
        const earlyAccess = list === 'early-access';
        const utm = UTM_FIELDS.filter(([key]) => entry.utm?.[key]);
        return html`
            <div class="card mb-3">
                <div class="card-header d-flex align-items-center gap-2">
                    <strong>${listLabel(list)}</strong>
                    ${
                        isPending(entry)
                            ? html`<span class="badge text-bg-warning">pending</span>`
                            : html`<span class="badge text-bg-success">confirmed</span>`
                    }
                    ${
                        earlyAccess && entry.invitedAt
                            ? html`<span class="badge text-bg-secondary">invited</span>`
                            : nothing
                    }
                </div>
                <div class="card-body">
                    <dl class="row mb-0">
                        ${
                            earlyAccess
                                ? html`<dt class="col-sm-4 text-muted">GM</dt>
                                      <dd class="col-sm-8">
                                          ${entry.gm ? "Yes — they'd like to run games during the beta" : 'No'}
                                      </dd>`
                                : nothing
                        }
                        <dt class="col-sm-4 text-muted">Source</dt>
                        <dd class="col-sm-8">${sourceSiteLabel(entry.sourceSite)}</dd>
                        ${utm.map(
                            ([key, label]) => html`
                                <dt class="col-sm-4 text-muted">${label}</dt>
                                <dd class="col-sm-8">${entry.utm[key]}</dd>
                            `,
                        )}
                        <dt class="col-sm-4 text-muted">Signed up</dt>
                        <dd class="col-sm-8">${formatDate(entry.createdAt)}</dd>
                        <dt class="col-sm-4 text-muted">Confirmed</dt>
                        <dd class="col-sm-8">
                            ${
                                isPending(entry)
                                    ? "Not yet — they haven't clicked the confirmation link"
                                    : formatDate(entry.confirmedAt)
                            }
                        </dd>
                        ${
                            earlyAccess
                                ? html`<dt class="col-sm-4 text-muted">Invited</dt>
                                      <dd class="col-sm-8">
                                          ${entry.invitedAt ? formatDate(entry.invitedAt) : 'Not yet'}
                                      </dd>`
                                : nothing
                        }
                        <dt class="col-sm-4 text-muted">Consent version</dt>
                        <dd class="col-sm-8">${entry.consentVersion}</dd>
                    </dl>
                </div>
                <div class="card-footer">${this._renderListActions(list)}</div>
            </div>
        `;
    }

    _renderListActions(list) {
        const confirming = this._confirming;
        if (confirming?.list === list) {
            const question =
                confirming.action === 'invite'
                    ? html`Invite ${this._contact.email} as
                          <strong>${invitationTypeLabel(this._type)}</strong>? d3-auth emails them.`
                    : html`Take ${this._contact.email} off ${listLabel(list)}?`;
            const buttons =
                confirming.action === 'invite'
                    ? this._renderConfirmButtons(
                          'Invite now',
                          'Inviting…',
                          this._invite,
                          'btn-primary',
                      )
                    : this._renderConfirmButtons(
                          'Remove',
                          'Removing…',
                          () => this._removeFromList(list),
                          'btn-danger',
                      );
            return html`<div class="d-flex flex-wrap align-items-center gap-2">
                <span class="small">${question}</span>${buttons}
            </div>`;
        }

        const invitable = list === 'early-access' && invitableEntry(this._contact);
        return html`
            <div class="d-flex flex-wrap align-items-center gap-2">
                ${
                    invitable
                        ? html`
                              <select
                                  class="form-select form-select-sm w-auto"
                                  aria-label="Invitation type"
                                  .value=${this._type}
                                  ?disabled=${this._acting || confirming}
                                  @change=${(e) => {
                                      this._type = e.target.value;
                                  }}
                              >
                                  ${INVITATION_TYPES.map(
                                      (
                                          t,
                                      ) => html`<option value=${t.slug} ?selected=${t.slug === this._type}>
                                          ${t.label}
                                      </option>`,
                                  )}
                              </select>
                              <button
                                  class="btn btn-sm btn-primary"
                                  type="button"
                                  ?disabled=${this._acting || confirming}
                                  @click=${() => this._confirm({ action: 'invite', list })}
                              >
                                  Invite
                              </button>
                          `
                        : nothing
                }
                <button
                    class="btn btn-sm btn-outline-danger ms-auto"
                    type="button"
                    ?disabled=${this._acting || confirming}
                    @click=${() => this._confirm({ action: 'remove-list', list })}
                >
                    Remove from ${listLabel(list)}
                </button>
            </div>
        `;
    }
}

customElements.define('d3-list-contact-view', ListContactView);
